/**
 * @file label-sheet-form.ts
 * @description Форма калибровки листов самоклейки склада — чистые правила
 *   (phase19.22, W17-5a/5b; ui-design §3): строки полей ↔ числа, проверка
 *   диапазона 0..50 мм с шагом 0.1 и предупреждение «> 15 мм — наклейки не
 *   поместятся».
 *
 * Поля ввода держат строки (кладовщик набирает «0,5» с запятой), сервер ждёт
 * числа — перевод и проверка живут здесь, а не в разметке карточки.
 */

import type { LabelSheetDto, LabelSheetMargins, LabelTemplate } from "../../../lib/api-labels";

/** Поля калибровки одного шаблона — строками, как в полях ввода. */
export type MarginDraft = Record<keyof LabelSheetMargins, string>;

/** Порядок и подписи полей карточки — данными. */
export const MARGIN_FIELDS: { key: keyof LabelSheetMargins; label: string }[] = [
  { key: "marginTopMm", label: "Поле сверху, мм" },
  { key: "marginLeftMm", label: "Поле слева, мм" },
  { key: "gapXMm", label: "Зазор по горизонтали, мм" },
  { key: "gapYMm", label: "Зазор по вертикали, мм" },
];

/** Больше этого поля/зазора — наклейки почти наверняка не поместятся. */
export const MARGIN_WARN_MM = 15;
const MARGIN_MAX_MM = 50;

export function toDraft(m: LabelSheetMargins): MarginDraft {
  return {
    marginTopMm: String(m.marginTopMm),
    marginLeftMm: String(m.marginLeftMm),
    gapXMm: String(m.gapXMm),
    gapYMm: String(m.gapYMm),
  };
}

/** «0,5» → 0.5; мусор, минус или больше 50 — `null`. Округление до 0.1. */
export function parseMm(value: string): number | null {
  const n = Number(value.trim().replace(",", "."));
  if (!Number.isFinite(n) || n < 0 || n > MARGIN_MAX_MM || value.trim() === "") return null;
  return Math.round(n * 10) / 10;
}

/** Черновик → числа; хоть одно поле битое — `null`. */
export function fromDraft(draft: MarginDraft): LabelSheetMargins | null {
  const out: Partial<LabelSheetMargins> = {};
  for (const { key } of MARGIN_FIELDS) {
    const n = parseMm(draft[key]);
    if (n === null) return null;
    out[key] = n;
  }
  return out as LabelSheetMargins;
}

/** Предупреждение по черновику шаблона; `null` — всё в порядке. */
export function draftWarning(draft: MarginDraft): string | null {
  if (!fromDraft(draft)) return "Поля — числа от 0 до 50 мм, например 0.5";
  const tooBig = MARGIN_FIELDS.some(({ key }) => (parseMm(draft[key]) ?? 0) > MARGIN_WARN_MM);
  return tooBig ? "Больше 15 мм — наклейки не поместятся на лист" : null;
}

/** Черновики всех шаблонов по ответу сервера. */
export function draftsFrom(sheets: LabelSheetDto[]): Record<LabelTemplate, MarginDraft> {
  return Object.fromEntries(sheets.map((s) => [s.template, toDraft(s)])) as Record<
    LabelTemplate,
    MarginDraft
  >;
}
